
import { PropType, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import numeral from 'numeral';
import useTokens from '@/composables/useTokens';
import useNumbers from '@/composables/useNumbers';
import { getAddress } from '@ethersproject/address';
import {
  PoolActivity,
  PoolActivityType
} from '@/services/balancer/subgraph/types';
import { ColumnDefinition } from '@/components/_global/BalTable/BalTable.vue';
import { formatDistanceToNow } from 'date-fns';
import { bnum } from '@balancer-labs/sor2/dist/bmath';
import useWeb3 from '@/services/web3/useWeb3';
import useBreakpoints from '@/composables/useBreakpoints';

type TokenAmount = {
  address: string;
  symbol: string;
  amount: string;
};

interface ActivityRow {
  label: string;
  value: string;
  timestamp: number;
  formattedDate: string;
  tx: string;
  type: PoolActivityType;
  tokenAmounts: TokenAmount[];
}

export default {
  emits: ['loadMore'],

  props: {
    tokens: {
      type: Array as PropType<string[]>,
      required: true
    },
    poolActivities: {
      type: Array as PropType<PoolActivity[]>,
      required: true
    },
    isLoading: {
      type: Boolean,
      default: false
    },
    isLoadingMore: {
      type: Boolean,
      default: false
    },
    loadMore: {
      type: Function as PropType<() => void>
    },
    isPaginated: {
      type: Boolean,
      default: false
    },
    noResultsLabel: {
      type: String
    }
  },

  setup(props) {
    const { fNum } = useNumbers();
    const { t } = useI18n();
    const { explorerLinks } = useWeb3();
    const { tokens, priceFor } = useTokens();
    const { upToLargeBreakpoint } = useBreakpoints();

    const columns = computed<ColumnDefinition<ActivityRow>[]>(() => [
      {
        name: t('action'),
        id: 'action',
        accessor: 'tx',
        Cell: 'actionCell',
        width: 175,
        sortable: false
      },
      {
        name: t('details'),
        id: 'details',
        accessor: '',
        Cell: 'detailsCell',
        width: 300,
        sortable: false
      },
      {
        name: t('value'),
        id: 'value',
        accessor: 'value',
        Cell: 'valueCell',
        align: 'right',
        className: 'align-center w-40',
        sortKey: pool => numeral(pool.value).value(),
        width: 125
      },
      {
        name: t('time'),
        id: 'timeAgo',
        accessor: 'timestamp',
        Cell: 'timeCell',
        align: 'right',
        sortKey: pool => numeral(pool.timestamp).value(),
        width: 200
      }
    ]);

    const activityRows = computed<ActivityRow[]>(() =>
      props.isLoading
        ? []
        : props.poolActivities.map(({ type, timestamp, tx, amounts }) => {
            const isJoin = type === 'Join';

            return {
              label: isJoin ? t('invest') : t('withdraw'),
              value: fNum(getJoinExitValue(amounts), 'usd'),
              timestamp,
              formattedDate: t('timeAgo', [formatDistanceToNow(timestamp)]),
              tx,
              type,
              tokenAmounts: getJoinExitDetails(amounts)
            };
          })
    );

    function getJoinExitValue(amounts: PoolActivity['amounts']) {
      return amounts
        .reduce((total, amount, index) => {
          const address = getAddress(props.tokens[index]);
          const token = tokens.value[address];
          const price = priceFor(token.address);
          const amountNumber = bnum(Math.abs(parseFloat(amount)));

          return total.plus(amountNumber.times(price));
        }, bnum(0))
        .toString();
    }

    function getJoinExitDetails(amounts: PoolActivity['amounts']) {
      return amounts.map((amount, index) => {
        const address = getAddress(props.tokens[index]);
        const token = tokens.value[address];
        const symbol = token ? token.symbol : address;
        const amountNumber = parseFloat(amount);

        return {
          address,
          symbol,
          amount: fNum(amountNumber, 'token')
        };
      });
    }

    return {
      columns,
      activityRows,
      explorer: explorerLinks,
      fNum,
      upToLargeBreakpoint
    };
  }
};
